import { byLabel } from 'module/helpers/comparators/by-label';
import { ClassNameOnlyProps } from 'module/types/common';
import { MultiUserField } from 'module/types/filter-field/field/filterable-field';
import { Select } from '@rocketlane/ui';
import { FC, ReactNode, RefObject, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Popover } from '../components/components';
import { renderFieldEditorUserAvatar } from '../components/field-editor-user-avatar';
import { UserFieldOptionsGetter } from '../types';
import type { UserOption } from '@rocketlane/ui';
import type { SelectInstance } from 'react-select';

type Props = ClassNameOnlyProps & {
  field: MultiUserField;
  title: ReactNode;
  optionsGetter: UserFieldOptionsGetter;
  onChange: (latest: number[]) => void;
  selectRef: RefObject<SelectInstance>;
};

interface FieldEditorUserOption extends UserOption {
  value: number;
  logoUrl?: string;
}

const StyledPopover = styled(Popover)`
  &&& {
    height: 400px;
  }
`;

export const ControlledMultiUserFieldEditorPopover: FC<
  React.PropsWithChildren<Props>
> = ({ field, title, onChange, optionsGetter, selectRef, className }) => {
  const options = useMemo(() => optionsGetter(field), [field, optionsGetter]);
  const onValueChange = useCallback(
    (latest: readonly FieldEditorUserOption[]) => {
      return onChange?.([...latest].sort(byLabel).map((x) => x.value));
    },
    [onChange],
  );

  const usersInOptions = useMemo(
    () => options.flatMap((option) => option.options),
    [options],
  );

  const selectedUsers = useMemo(
    () =>
      (field.value ?? [])
        .map((id) => usersInOptions?.find((u) => u?.value === id))
        .filter(Boolean),
    [field.value, usersInOptions],
  );

  return (
    <StyledPopover className={className}>
      <Popover.Title>{title}</Popover.Title>
      <Select<FieldEditorUserOption, true>
        closeMenuOnSelect={false}
        collapsible={false}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isMulti
        menuFlush
        menuIsOpen
        menuPortalTarget={null}
        renderOptionIcon={renderFieldEditorUserAvatar}
        ref={selectRef}
        options={options}
        value={selectedUsers}
        onChange={onValueChange}
        searchControl
        variant="user"
        virtualized
      />
    </StyledPopover>
  );
};
